// 内存管理服务 - 优化内存使用，防止内存泄漏
class MemoryManager {
  constructor() {
    this.memoryStats = {
      used: 0,
      total: 0,
      limit: 0,
      usagePercent: 0,
      peak: 0,
      leaks: 0
    }
    
    this.cleanupTasks = new Set()
    this.weakRefs = new WeakMap()
    this.observers = new Set()
    this.isMonitoring = false
    this.cleanupInterval = 30000 // 30秒清理一次
    this.monitorInterval = 5000 // 5秒监控一次
    
    this.thresholds = {
      warning: 70, // 70% 警告
      critical: 85, // 85% 严重
      maxLeaks: 10 // 最大泄漏数量
    }
    
    this.init()
  }
  
  // 初始化内存管理
  init() {
    if (this.isMonitoring) return
    
    this.isMonitoring = true
    this.startMonitoring()
    this.startCleanup()
    this.setupMemoryObservers()
    
    console.log('✅ 内存管理系统已启动')
  }
  
  // 开始内存监控
  startMonitoring() {
    setInterval(() => {
      this.updateMemoryStats()
      this.checkMemoryHealth()
    }, this.monitorInterval)
  }
  
  // 更新内存统计
  updateMemoryStats() {
    if ('memory' in performance) {
      const memory = performance.memory
      this.memoryStats.used = memory.usedJSHeapSize
      this.memoryStats.total = memory.totalJSHeapSize
      this.memoryStats.limit = memory.jsHeapSizeLimit
      this.memoryStats.usagePercent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100
      
      // 更新峰值
      if (this.memoryStats.used > this.memoryStats.peak) {
        this.memoryStats.peak = this.memoryStats.used
      }
    }
  }
  
  // 检查内存健康状态
  checkMemoryHealth() {
    const { usagePercent, leaks } = this.memoryStats
    
    if (usagePercent > this.thresholds.critical) {
      this.handleCriticalMemory(usagePercent)
    } else if (usagePercent > this.thresholds.warning) {
      this.handleMemoryWarning(usagePercent)
    }
    
    if (leaks > this.thresholds.maxLeaks) {
      this.handleMemoryLeaks(leaks)
    }
  }
  
  // 开始清理任务
  startCleanup() {
    setInterval(() => {
      this.performCleanup()
    }, this.cleanupInterval)
  }
  
  // 执行内存清理
  performCleanup() {
    console.log('🧹 开始内存清理...')
    
    let cleanedCount = 0
    
    // 清理过期的弱引用
    this.cleanupWeakRefs()
    cleanedCount++
    
    // 执行清理任务
    this.cleanupTasks.forEach(task => {
      try {
        if (typeof task === 'function') {
          task()
          cleanedCount++
        }
      } catch (error) {
        console.warn('清理任务执行失败:', error)
      }
    })
    
    // 清理事件监听器
    this.cleanupEventListeners()
    cleanedCount++
    
    // 清理定时器
    this.cleanupTimers()
    cleanedCount++
    
    // 强制垃圾回收（如果可用）
    this.forceGarbageCollection()
    
    console.log(`✅ 内存清理完成，清理了 ${cleanedCount} 项`)
    
    // 通知观察者
    this.notifyObservers('cleanup', { cleanedCount })
  }
  
  // 清理弱引用
  cleanupWeakRefs() {
    // 这里可以实现弱引用的清理逻辑
    // 由于 JavaScript 的 WeakMap 和 WeakSet 会自动清理，这里主要是清理自定义的弱引用
  }
  
  // 清理事件监听器
  cleanupEventListeners() {
    // 清理可能泄漏的事件监听器
    // 这里可以维护一个事件监听器列表，清理不再需要的监听器
  }
  
  // 清理定时器
  cleanupTimers() {
    // 清理可能泄漏的定时器
    // 这里可以维护一个定时器列表，清理不再需要的定时器
  }
  
  // 强制垃圾回收
  forceGarbageCollection() {
    if (window.gc) {
      try {
        window.gc()
        console.log('🗑️ 强制垃圾回收已执行')
      } catch (error) {
        console.warn('强制垃圾回收失败:', error)
      }
    }
  }
  
  // 设置内存观察器
  setupMemoryObservers() {
    // 监听内存相关事件
    if (window.addEventListener) {
      window.addEventListener('beforeunload', () => {
        this.performCleanup()
      })
      
      window.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          // 页面隐藏时执行清理
          this.performCleanup()
        }
      })
    }
  }
  
  // 处理内存警告
  handleMemoryWarning(usagePercent) {
    console.warn(`⚠️ 内存使用率警告: ${usagePercent.toFixed(2)}%`)
    
    // 执行轻度清理
    this.performLightCleanup()
    
    // 通知观察者
    this.notifyObservers('memoryWarning', { usagePercent })
  }
  
  // 处理严重内存问题
  handleCriticalMemory(usagePercent) {
    console.error(`🚨 内存使用率严重: ${usagePercent.toFixed(2)}%`)
    
    // 执行紧急清理
    this.performEmergencyCleanup()
    
    // 通知观察者
    this.notifyObservers('criticalMemory', { usagePercent })
  }
  
  // 处理内存泄漏
  handleMemoryLeaks(leakCount) {
    console.error(`🚨 检测到内存泄漏: ${leakCount} 个`)
    
    // 执行泄漏清理
    this.performLeakCleanup()
    
    // 通知观察者
    this.notifyObservers('memoryLeaks', { leakCount })
  }
  
  // 执行轻度清理
  performLightCleanup() {
    console.log('🧹 执行轻度内存清理...')
    
    // 清理缓存
    this.clearCaches()
    
    // 清理过期的数据
    this.clearExpiredData()
    
    // 清理未使用的 DOM 元素
    this.clearUnusedElements()
  }
  
  // 执行紧急清理
  performEmergencyCleanup() {
    console.log('🚨 执行紧急内存清理...')
    
    // 执行所有清理任务
    this.performLightCleanup()
    
    // 清理所有缓存
    this.clearAllCaches()
    
    // 清理所有定时器
    this.clearAllTimers()
    
    // 强制垃圾回收
    this.forceGarbageCollection()
  }
  
  // 执行泄漏清理
  performLeakCleanup() {
    console.log('🔍 执行内存泄漏清理...')
    
    // 清理循环引用
    this.clearCircularReferences()
    
    // 清理未释放的资源
    this.clearUnreleasedResources()
    
    // 清理事件监听器
    this.clearEventListeners()
  }
  
  // 清理缓存
  clearCaches() {
    // 清理应用缓存
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          caches.delete(cacheName)
        })
      })
    }
    
    // 清理 localStorage 中的过期数据
    this.clearExpiredLocalStorage()
  }
  
  // 清理所有缓存
  clearAllCaches() {
    // 清理所有缓存
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          caches.delete(cacheName)
        })
      })
    }
    
    // 清理 localStorage
    localStorage.clear()
    
    // 清理 sessionStorage
    sessionStorage.clear()
  }
  
  // 清理过期数据
  clearExpiredData() {
    // 清理过期的 IndexedDB 数据
    this.clearExpiredIndexedDB()
    
    // 清理过期的内存数据
    this.clearExpiredMemoryData()
  }
  
  // 清理过期的 localStorage
  clearExpiredLocalStorage() {
    const now = Date.now()
    const keysToRemove = []
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key) {
        try {
          const value = localStorage.getItem(key)
          const data = JSON.parse(value)
          
          // 检查是否有过期时间
          if (data.expires && data.expires < now) {
            keysToRemove.push(key)
          }
        } catch (error) {
          // 忽略解析错误
        }
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key)
    })
  }
  
  // 清理过期的 IndexedDB 数据
  clearExpiredIndexedDB() {
    // 这里可以实现 IndexedDB 的过期数据清理
    // 需要根据具体的 IndexedDB 结构来实现
  }
  
  // 清理过期的内存数据
  clearExpiredMemoryData() {
    // 清理过期的内存数据
    // 这里可以维护一个内存数据列表，清理过期的数据
  }
  
  // 清理未使用的 DOM 元素
  clearUnusedElements() {
    // 清理未使用的 DOM 元素
    // 这里可以实现 DOM 元素的清理逻辑
  }
  
  // 清理所有定时器
  clearAllTimers() {
    // 清理所有定时器
    // 这里可以维护一个定时器列表，清理所有定时器
  }
  
  // 清理循环引用
  clearCircularReferences() {
    // 清理循环引用
    // 这里可以实现循环引用的检测和清理
  }
  
  // 清理未释放的资源
  clearUnreleasedResources() {
    // 清理未释放的资源
    // 这里可以实现资源泄漏的检测和清理
  }
  
  // 清理事件监听器
  clearEventListeners() {
    // 清理事件监听器
    // 这里可以实现事件监听器的清理
  }
  
  // 添加清理任务
  addCleanupTask(task) {
    if (typeof task === 'function') {
      this.cleanupTasks.add(task)
    }
  }
  
  // 移除清理任务
  removeCleanupTask(task) {
    this.cleanupTasks.delete(task)
  }
  
  // 添加观察者
  addObserver(observer) {
    if (typeof observer === 'function') {
      this.observers.add(observer)
    }
  }
  
  // 移除观察者
  removeObserver(observer) {
    this.observers.delete(observer)
  }
  
  // 通知观察者
  notifyObservers(event, data) {
    this.observers.forEach(observer => {
      try {
        observer(event, data)
      } catch (error) {
        console.warn('观察者通知失败:', error)
      }
    })
  }
  
  // 创建弱引用
  createWeakRef(obj) {
    if (typeof WeakRef !== 'undefined') {
      return new WeakRef(obj)
    }
    return obj
  }
  
  // 检查弱引用是否有效
  isWeakRefValid(weakRef) {
    if (typeof WeakRef !== 'undefined' && weakRef instanceof WeakRef) {
      return weakRef.deref() !== undefined
    }
    return true
  }
  
  // 获取内存统计
  getMemoryStats() {
    return { ...this.memoryStats }
  }
  
  // 获取内存使用率
  getMemoryUsagePercent() {
    return this.memoryStats.usagePercent
  }
  
  // 检查是否有内存泄漏
  hasMemoryLeaks() {
    return this.memoryStats.leaks > this.thresholds.maxLeaks
  }
  
  // 获取内存建议
  getMemoryRecommendations() {
    const recommendations = []
    const { usagePercent, leaks } = this.memoryStats
    
    if (usagePercent > this.thresholds.critical) {
      recommendations.push('内存使用率过高，建议立即清理内存')
    } else if (usagePercent > this.thresholds.warning) {
      recommendations.push('内存使用率较高，建议定期清理内存')
    }
    
    if (leaks > this.thresholds.maxLeaks) {
      recommendations.push('检测到内存泄漏，建议检查代码中的循环引用')
    }
    
    if (this.memoryStats.peak > this.memoryStats.limit * 0.8) {
      recommendations.push('内存峰值过高，建议优化内存使用')
    }
    
    return recommendations
  }
  
  // 重置内存统计
  resetMemoryStats() {
    this.memoryStats = {
      used: 0,
      total: 0,
      limit: 0,
      usagePercent: 0,
      peak: 0,
      leaks: 0
    }
  }
  
  // 停止监控
  stop() {
    this.isMonitoring = false
    this.cleanupTasks.clear()
    this.observers.clear()
    
    console.log('⏹️ 内存管理系统已停止')
  }
  
  // 销毁管理器
  destroy() {
    this.stop()
    this.resetMemoryStats()
  }
}

// 创建单例实例
const memoryManager = new MemoryManager()

export default memoryManager

